import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, MapPin, Maximize2, BedDouble, Phone, Loader2, Check,
} from "lucide-react";
import { LogoMark, WhatsAppIcon } from "../components/Logo";
import { ShareMenu } from "../components/ShareButton";
import { ListingCard } from "../components/Listings";
import { getListing, getListings, resolveImage, logEnquiry } from "../lib/api";
import { waLink, telLink, BRAND } from "../lib/brand";

const dealTone = {
  Sale: "bg-[#131311] text-[#faf3e0]",
  Purchase: "bg-[#9a7b38] text-white",
  Rent: "bg-[#25d366] text-white",
};

const HIGHLIGHTS = [
  "Clear title & verified documents",
  "On-ground guidance from site visit to registry",
  "Transparent, fair-market pricing",
  "Trusted across Hisar for a generation",
];

export default function PropertyDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setActive(0);
    getListing(id)
      .then((l) => {
        setListing(l);
        return getListings().then((all) => {
          const others = all.filter((x) => x.id !== l.id);
          const same = others.filter((x) => x.category === l.category);
          const rest = others.filter((x) => x.category !== l.category);
          setRelated([...same, ...rest].slice(0, 3));
        });
      })
      .catch(() => setListing(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#faf9f5]"><Loader2 className="h-6 w-6 animate-spin text-[#9a7b38]" /></div>;
  }

  if (!listing) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#faf9f5] px-6 text-center">
        <div data-testid="property-not-found">
          <h1 className="font-serif text-3xl font-semibold text-[#131311]">Property not found</h1>
          <p className="text-[#8a8578] mt-2">This listing may have been removed.</p>
          <Link to="/properties" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#131311] text-[#faf9f5] px-6 py-3 text-sm font-semibold hover:bg-[#9a7b38] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Browse all properties
          </Link>
        </div>
      </div>
    );
  }

  const gallery = listing.images && listing.images.length
    ? listing.images
    : (listing.image_url ? [listing.image_url] : []);
  const mainImg = gallery[active] || listing.image_url;

  const enquire = waLink(
    `Hello Laxmi Jiwan Properties, I'm interested in "${listing.title}" (${listing.location}${listing.price ? `, ${listing.price}` : ""}). Please share more details.`
  );

  return (
    <div className="min-h-screen bg-[#faf9f5] text-[#131311]">
      <header className="sticky top-0 z-40 bg-[#faf9f5]/85 backdrop-blur-xl border-b border-[#e7e3d7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="detail-logo">
            <LogoMark className="h-9 w-9" />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-lg font-semibold tracking-tight">Laxmi Jiwan</span>
              <span className="text-[9px] tracking-[0.42em] uppercase text-[#9a7b38] font-medium mt-0.5">Properties · Hisar</span>
            </span>
          </Link>
          <Link to="/properties" className="inline-flex items-center gap-2 rounded-full border border-[#e0dccf] px-5 py-2.5 text-sm font-medium text-[#52514c] hover:text-[#131311] hover:border-[#c5a059] transition-colors" data-testid="detail-back">
            <ArrowLeft className="h-4 w-4" /> All properties
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-14" data-testid="property-detail">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-14">
          {/* Gallery */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative rounded-[2rem] p-2 bg-gradient-to-br from-[#f0d98a] via-[#c5a059] to-[#9a7b38] shadow-[0_40px_80px_-40px_rgba(19,19,17,0.4)]">
              <div className="relative overflow-hidden rounded-[1.7rem] aspect-[16/10] bg-[#0b0b0a]">
                {mainImg && (
                  <img
                    src={resolveImage(mainImg)}
                    alt={listing.title}
                    className="h-full w-full object-cover"
                    data-testid="detail-main-image"
                  />
                )}
                <span className={`absolute top-4 left-4 rounded-full px-3 py-1 text-[11px] font-semibold ${dealTone[listing.deal_type] || "bg-[#131311] text-white"}`}>
                  For {listing.deal_type}
                </span>
              </div>
            </div>
            {gallery.length > 1 && (
              <div className="mt-4 flex gap-3 flex-wrap" data-testid="detail-thumbnails">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    className={`h-20 w-28 rounded-xl overflow-hidden border-2 transition-all ${active === i ? "border-[#9a7b38]" : "border-transparent opacity-70 hover:opacity-100"}`}
                    data-testid={`detail-thumb-${i}`}
                  >
                    <img src={resolveImage(img)} alt={`${listing.title} ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:sticky lg:top-28 self-start"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#9a7b38] font-semibold">
              {listing.category}
            </div>
            <h1 className="mt-2 font-serif text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.02]">
              {listing.title}
            </h1>
            <div className="mt-3 flex items-center gap-2 text-[#52514c]">
              <MapPin className="h-4 w-4 text-[#9a7b38]" /> {listing.location}
            </div>

            {listing.price && (
              <div className="mt-6 font-serif text-3xl font-semibold text-gold-gradient">
                {listing.price}
              </div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-3">
              {listing.area && (
                <div className="rounded-2xl border border-[#e7e3d7] bg-white px-4 py-3">
                  <div className="text-[11px] uppercase tracking-wide text-[#8a8578]">Area</div>
                  <div className="mt-1 flex items-center gap-2 font-semibold"><Maximize2 className="h-4 w-4 text-[#9a7b38]" /> {listing.area}</div>
                </div>
              )}
              {listing.bedrooms && (
                <div className="rounded-2xl border border-[#e7e3d7] bg-white px-4 py-3">
                  <div className="text-[11px] uppercase tracking-wide text-[#8a8578]">Configuration</div>
                  <div className="mt-1 flex items-center gap-2 font-semibold"><BedDouble className="h-4 w-4 text-[#9a7b38]" /> {listing.bedrooms}</div>
                </div>
              )}
            </div>

            {listing.description && (
              <p className="mt-6 text-[#52514c] leading-relaxed">{listing.description}</p>
            )}

            <div className="mt-6 space-y-2.5">
              {HIGHLIGHTS.map((h) => (
                <div key={h} className="flex items-start gap-2.5 text-sm text-[#3a382f]">
                  <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#f4efdf] grid place-items-center text-[#9a7b38]"><Check className="h-3 w-3" /></span>
                  {h}
                </div>
              ))}
            </div>

            <div className="mt-8 space-y-3">
              <a
                href={enquire}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logEnquiry({ listing_id: listing.id, title: listing.title, location: listing.location, source: "detail" })}
                className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25d366] text-white px-7 py-4 text-sm font-semibold shadow-[0_12px_30px_rgba(37,211,102,0.28)] hover:bg-[#1eae53] transition-all duration-300"
                data-testid="detail-enquire-whatsapp"
              >
                <WhatsAppIcon className="h-5 w-5" /> Enquire on WhatsApp
              </a>
              <div className="flex gap-3">
                <a
                  href={telLink}
                  className="flex-1 inline-flex items-center justify-center gap-2.5 rounded-full border border-[#131311] px-7 py-4 text-sm font-semibold hover:bg-[#131311] hover:text-[#faf9f5] transition-all duration-300"
                  data-testid="detail-call"
                >
                  <Phone className="h-4 w-4" /> Call
                </a>
                <ShareMenu slug={listing.slug || listing.id} title={listing.title} wrapperClassName="flex-1" />
              </div>
            </div>
            <p className="mt-4 text-xs text-[#6b675c]">{BRAND.address}</p>
          </motion.div>
        </div>
      </main>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-16" data-testid="related-properties">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#c5a059]" />
            <span className="text-xs tracking-[0.34em] uppercase text-[#9a7b38] font-semibold">
              Similar Properties in Hisar
            </span>
          </div>
          {/* Mobile: swipeable */}
          <div className="sm:hidden flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {related.map((l) => (
              <div key={l.id} className="snap-start shrink-0"><ListingCard l={l} /></div>
            ))}
          </div>
          {/* Desktop: grid */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center sm:justify-items-stretch">
            {related.map((l) => (
              <div key={l.id} className="w-full max-w-[340px]"><ListingCard l={l} /></div>
            ))}
          </div>
        </section>
      )}

      <footer className="mt-6 border-t border-[#e7e3d7] bg-[#131311] text-[#faf9f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <h3 className="font-serif text-2xl sm:text-3xl font-semibold">
              Looking for something <span className="italic text-gold-gradient">different?</span>
            </h3>
            <p className="text-[#c9c4b6] mt-2 text-sm">Tell us what you need — we'll find the right property in Hisar.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/properties" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#c5a059] text-[#faf3e0] px-6 py-3.5 text-sm font-semibold hover:bg-[#c5a059] hover:text-[#131311] transition-colors" data-testid="detail-browse-all">
              <ArrowLeft className="h-4 w-4" /> Browse all properties
            </Link>
            <a
              href={enquire}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => logEnquiry({ listing_id: listing.id, title: listing.title, location: listing.location, source: "detail" })}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25d366] text-white px-6 py-3.5 text-sm font-semibold hover:bg-[#1eae53] transition-colors"
            >
              <WhatsAppIcon className="h-5 w-5" /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
